/* rr-share-cart.js — Race & Rally (Dawn-based)
   Adds "Share cart" functionality for any button with [data-share-cart].
   Works in cart drawer and cart page. */

(function () {
  const SELECTOR_BUTTON = '[data-share-cart]';
  const TOAST_CLASS = 'rr-share-cart__toast';
  const BUTTON_BUSY_ATTR = 'data-busy';

  // ---- Helpers -------------------------------------------------------------

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to load cart.');
    return res.json();
  }

  // Build native /cart/{variantId:qty,...} permalink from /cart.js payload
  function buildCartPermalink(cart) {
    if (!cart?.items?.length) return null;

    // In /cart.js, the variant id is "id" (number)
    const pairs = cart.items
      .filter(i => Number.isFinite(i.id) && i.quantity > 0)
      .map(i => `${i.id}:${i.quantity}`)
      .join(',');

    if (!pairs) return null;

    const origin = window.location.origin;
    let url = `${origin}/cart/${pairs}`;

    // Optional: append a discount code if you ever want to pre-apply one
    // const code = 'RACE10';
    // if (code) url += `${url.includes('?') ? '&' : '?'}discount=${encodeURIComponent(code)}`;

    return url;
  }

  function ensureToastEl(scopeEl) {
    // Reuse existing toast if present; otherwise create a minimal one beside the button
    let toast = scopeEl.querySelector(`.${TOAST_CLASS}`);
    if (!toast) {
      toast = document.createElement('span');
      toast.className = TOAST_CLASS;
      toast.hidden = true;
      toast.textContent = 'Link copied to clipboard';
      scopeEl.appendChild(toast);
    }
    return toast;
  }

  function showToast(toastEl, msg = 'Link copied to clipboard') {
    if (!toastEl) return;
    toastEl.textContent = msg;
    toastEl.hidden = false;
    clearTimeout(showToast._t);
    showToast._t = setTimeout(() => (toastEl.hidden = true), 1800);
  }

  async function copyToClipboard(text) {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Legacy fallback using a temporary input
    const tmp = document.createElement('input');
    tmp.value = text;
    tmp.setAttribute('readonly', '');
    tmp.style.position = 'absolute';
    tmp.style.left = '-9999px';
    document.body.appendChild(tmp);
    tmp.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(tmp);
    return ok;
  }

  async function onShareClick(button) {
    if (!button || button.getAttribute(BUTTON_BUSY_ATTR) === '1') return;
    button.setAttribute(BUTTON_BUSY_ATTR, '1');

    // Find a sensible scope for toast injection (parent wrapper or the button itself)
    const scope = button.closest('.rr-share-cart') || button.parentElement || button;
    const toastEl = ensureToastEl(scope);

    try {
      const cart = await fetchCart();
      const url = buildCartPermalink(cart);

      if (!url) {
        showToast(toastEl, 'Cart is empty');
        return;
      }

      // Prefer native Web Share on mobile
      const isMobileUA = /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);
      if (navigator.share && isMobileUA) {
        try {
          await navigator.share({ title: document.title || 'My cart', url });
          return; // Shared via native sheet
        } catch {
          // fall through to clipboard
        }
      }

      const ok = await copyToClipboard(url);
      showToast(toastEl, ok ? 'Link copied to clipboard' : url); // if copy failed, at least reveal the URL
    } catch (err) {
      console.error('[RR Share Cart] Error:', err);
      showToast(toastEl, 'Could not create link');
    } finally {
      button.removeAttribute(BUTTON_BUSY_ATTR);
    }
  }

  function bindShareButtons(root = document) {
    root.querySelectorAll(SELECTOR_BUTTON).forEach(btn => {
      if (btn.dataset._shareBound) return;
      btn.addEventListener('click', () => onShareClick(btn));
      btn.dataset._shareBound = '1';
    });
  }

  // ---- Init & Rebind hooks -------------------------------------------------

  document.addEventListener('DOMContentLoaded', () => bindShareButtons());
  document.addEventListener('cart:updated', () => bindShareButtons());
  document.addEventListener('shopify:section:load', (e) => bindShareButtons(e.target));

  // Expose a tiny API if you want to trigger a manual rebind elsewhere
  window.RRShareCart = {
    rebind: bindShareButtons
  };
})();
